import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Form, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import StepsHeading from './StepsHeading';
import { AddItemButton, CustomInput, CustomSelect } from '../../../components';
import { AddUsers, GetUsers, type UserData } from '../../../apis/onboarding';
import { useSpin } from '../../../components/SpinContext.jsx';
import { useToken } from '../../../common/helper';
import useUnauthorize from '../../../hooks/useUnauthorize';
import { legacyFillColor } from '@/utils/legacyColor';
interface UsersProps {
  onSuccess: () => void;
}
interface UsersRef {
  completeStep: () => void;
}

interface UserTableData {
  key: string;
  email: string;
  role: string;
  id?: string | null;
}

/**
 * Users onboarding step component
 *
 * Allows inviting team members during onboarding by entering their email addresses
 * and assigning permission levels (Admin, Member, Viewer)
 *
 * @param onSuccess - Callback called when users are successfully added
 */
const Users = forwardRef<UsersRef, UsersProps>(({ onSuccess }, ref) => {
  const [form] = Form.useForm();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [dataArr, setDataArr] = useState<UserTableData[]>([]);

  const onFinish = () => {};

  const fetchUsers = useCallback(() => {
    setDataArr([]);
    showSpin();
    GetUsers(token ?? '', false)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            const arr = data?.data?.data?.length ? data.data.data : [];
            if (arr.length > 0) {
              const newData: UserTableData[] = arr.map(
                (element: UserData, index: number) => ({
                  key: (index + 1).toString(),
                  email: element.email || '',
                  role:
                    element?.teams?.find(
                      (x) => x.name.toLowerCase() === 'default'
                    )?.role_name || '',
                  id: element.id || null,
                })
              );
              setDataArr(newData);
            } else {
              setDataArr([
                {
                  id: null,
                  email: '',
                  role: '',
                  key: '1',
                },
              ]);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, showSpin, token, unauthorize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (
    key: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDataArr((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, email: event.target.value } : item
      )
    );
  };

  const handleSelectChange = (key: string, value: string) => {
    setDataArr((prev) =>
      prev.map((item) => (item.key === key ? { ...item, role: value } : item))
    );
  };
  const submitUsers = (model: UserTableData[]) => {
    AddUsers(token ?? '', model, false)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            onSuccess();
            // message.success(data?.data?.message);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  //Expose specific methods to the parent through useImperativeHandle
  useImperativeHandle(ref, () => ({
    completeStep, // Expose the submitProducts method to the parent
  }));

  const completeStep = () => {
    form
      .validateFields()
      .then(() => {
        const values = form.getFieldsValue();
        const formattedData = Object.keys(values).map((key) => ({
          key,
          email: values[key].email,
          role: values[key].role || 'Admin', // fallback just in case
          id: dataArr.find((item) => item.key === key)?.id || null,
        }));

        if (formattedData.length > 0) {
          submitUsers(formattedData);
        } else {
          message.error(
            'Please invite at least one person from your organization.'
          );
        }
      })
      .catch(() => {});
  };

  const handleDeleteRow = (key: string) => {
    setDataArr((prev) => prev.filter((item) => item.key !== key));
    form.resetFields([
      [key, 'email'],
      [key, 'role'],
    ]);
  };

  const handleAddItem = () => {
    const newKey = Date.now().toString();
    const newItem = {
      key: newKey,
      email: '',
      role: '',
    };
    setDataArr((prev) => [...prev, newItem]);
  };

  return (
    <>
      <div className='onboarding-users-step'>
        <div className='default-steps-content'>
          <StepsHeading
            heading='Invite people from your organization'
            text='Invite your teammates to join your organization on Zoomlogi. They will be able to see all shipments being tracked by your linked couriers.'
          />
          <Form form={form} name='users' layout='vertical' onFinish={onFinish}>
            <div className='onboarding-users-grid'>
              <div className='onboarding-users-grid-header'>Email</div>
              <div className='onboarding-users-grid-header'>
                Permission level
              </div>
              <div className='onboarding-users-grid-header onboarding-users-action-header'>
                Action
              </div>

              {dataArr.map((record, index) => (
                <div className='onboarding-users-row' key={record.key}>
                  <Form.Item
                    name={[record.key, 'email']}
                    initialValue={record.email}
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Enter Valid Email Only' },
                    ]}
                    style={{ marginBottom: '0px' }}
                  >
                    <CustomInput
                      placeholder='Enter email'
                      height='36px'
                      border={index === 0 ? '2px solid #E8E8E8' : '0px'}
                      bgColor={legacyFillColor(
                        index === 0 ? '#fff' : '#f3f3f3'
                      )}
                      textColor='var(--text-secondary)'
                      borderRadius='8px'
                      disabled={index === 0}
                      placeholderColor='var(--text-secondary)'
                      lineHeight='38px'
                      onChange={(value) => handleInputChange(record.key, value)}
                    />
                  </Form.Item>

                  <Form.Item
                    name={[record.key, 'role']}
                    initialValue={record.role || (index === 0 ? 'Admin' : '')}
                    rules={[
                      { required: true, message: 'Please select option' },
                    ]}
                    style={{ marginBottom: '0' }}
                  >
                    <CustomSelect
                      placeholder='Select'
                      onChange={(value) =>
                        handleSelectChange(record.key, value)
                      }
                      width='100%'
                      height='36px'
                      className='custom-select-disabled'
                      border={index === 0 ? '2px solid #E8E8E8' : '0px'}
                      bgColor={index === 0 ? '#fff' : '#F3F3F3'}
                      disabled={index === 0}
                      textColor='var(--text-secondary)'
                      borderRadius='8px'
                      disableOption={true}
                      optionName='Admin'
                      options={[
                        { value: 'Admin', label: 'Admin' },
                        { value: 'Member', label: 'Member' },
                        { value: 'Viewer', label: 'Viewer' },
                      ]}
                    />
                  </Form.Item>

                  <button
                    aria-label='Remove user'
                    className='onboarding-users-remove'
                    disabled={index === 0}
                    onClick={() => handleDeleteRow(record.key)}
                    type='button'
                  >
                    <CloseOutlined />
                  </button>
                </div>
              ))}
            </div>

            <AddItemButton text='Add Another' onClick={handleAddItem} />
          </Form>
        </div>
      </div>
    </>
  );
});
Users.displayName = 'Users';

export default Users;
